import { Container } from "pixi.js";
import React from "react";

import Entity, { GameEvents } from "../entity/Entity";
import SpatialHashingBroadphase from "../physics/SpatialHashingBroadphase";
import { ReactEntity } from "../ReactEntity";

const SMOOTHING = 0.95;
export default class FPSMeter extends ReactEntity implements Entity {
  persistenceLevel = 100;
  averageDuration: number = 0;
  slowFrameCount: number = 0;
  lastUpdate = performance.now();

  visible = false;

  constructor() {
    super(() => {
      const {
        fps,
        fps2,
        bodyCount,
        hugeBodyCount,
        kinematicBodyCount,
        particleBodyCount,
        dynamicBodyCount,
        collisions,
        entityCount,
        spriteCount,
        contacts,
        memoryUsed,
        memoryTotal,
        gpuMemory,
        webglInfo,
        audioInfo,
      } = this.getStats();

      return (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 1000,
            opacity: this.visible ? 1 : 0,
          }}
        >
          <ul
            style={{
              color: "white",
              fontSize: "12px",
              fontFamily: "monospace",
              display: "flex",
              gap: "16px",
              margin: 0,
              padding: 0,
              listStyle: "none",
            }}
          >
            <li>
              fps: {fps} ({fps2})
            </li>
            <li>
              bodies: {bodyCount} ({kinematicBodyCount}, {particleBodyCount},{" "}
              {dynamicBodyCount}, {hugeBodyCount})
            </li>
            <li>collisions: {collisions}</li>
            <li>entities: {entityCount}</li>
            <li>sprites: {spriteCount}</li>
            <li>contacts: {contacts}</li>
            <li>
              js heap: {memoryUsed}MB / {memoryTotal}MB
            </li>
            <li>gpu memory: {gpuMemory}</li>
            <li>webgl: {webglInfo}</li>
            <li>audio: {audioInfo}</li>
          </ul>
        </div>
      );
    });
  }

  onAdd() {
    super.onAdd();
    this.averageDuration = 1 / 60;
  }

  onRender() {
    const now = performance.now();
    const duration = now - this.lastUpdate;
    this.averageDuration =
      SMOOTHING * this.averageDuration + (1.0 - SMOOTHING) * duration;
    this.lastUpdate = now;

    super.onRender();
  }

  getStats() {
    const world = this.game?.world;
    const broadphase = this.game?.world.broadphase as SpatialHashingBroadphase;

    // JS Memory stats (available in Chromium-based browsers)
    let memoryUsed = 0;
    let memoryTotal = 0;
    if ("memory" in performance) {
      const memory = (performance as any).memory;
      memoryUsed = Math.round(memory.usedJSHeapSize / 1024 / 1024);
      memoryTotal = Math.round(memory.totalJSHeapSize / 1024 / 1024);
    }

    // GPU Memory stats (WebGL context info)
    let gpuMemory = "N/A";
    let webglInfo = "N/A";
    try {
      const renderer = this.game?.renderer?.app?.renderer;
      if (renderer && "gl" in renderer) {
        const gl = (renderer as any).gl as WebGLRenderingContext;
        if (gl) {
          // Get texture count as a proxy for GPU memory usage
          const textures =
            (renderer as any).texture?.managedTextures?.length ?? 0;
          const geometries =
            (renderer as any).geometry?.managedGeometries?.length ?? 0;
          gpuMemory = `${textures} textures, ${geometries} geometries`;

          // Get WebGL resource usage
          const numPrograms = gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS);
          const numTextureUnits = gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS);
          const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
          const maxRenderBufferSize = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE);

          // Check WebGL memory extension (if available)
          const memInfo = gl.getExtension("WEBGL_memory_info");
          if (memInfo) {
            const totalMemory = (
              gl.getParameter(
                (memInfo as any).MEMORY_INFO_TOTAL_AVAILABLE_MEMORY_WEBGL,
              ) /
              1024 /
              1024
            ).toFixed(0);
            const currentMemory = (
              gl.getParameter(
                (memInfo as any).MEMORY_INFO_CURRENT_AVAILABLE_MEMORY_WEBGL,
              ) /
              1024 /
              1024
            ).toFixed(0);
            webglInfo = `${currentMemory}/${totalMemory}MB`;
          } else {
            // Estimate based on texture dimensions
            const pixiTextures =
              (renderer as any).texture?.managedTextures || [];
            let estimatedBytes = 0;
            for (const tex of pixiTextures) {
              if (tex?.baseTexture) {
                const width = tex.baseTexture.width || 0;
                const height = tex.baseTexture.height || 0;
                // Assume 4 bytes per pixel (RGBA)
                estimatedBytes += width * height * 4;
              }
            }
            const estimatedMB = (estimatedBytes / 1024 / 1024).toFixed(1);
            webglInfo = `~${estimatedMB}MB (est)`;
          }
        }
      }
    } catch (e) {
      gpuMemory = "Error reading GPU info";
      webglInfo = "Error";
    }

    // Audio context stats
    let audioInfo = "N/A";
    try {
      const audioContext = this.game?.audio;
      if (audioContext) {
        const state = audioContext.state;
        const sampleRate = audioContext.sampleRate;
        const currentTime = audioContext.currentTime.toFixed(1);

        // Count active audio nodes
        const audioNodes = this.game?.getAudioNodeCount() || 0;

        audioInfo = `${audioNodes} nodes, ${currentTime}s`;

        // Try to estimate audio buffer memory (very rough)
        // Audio nodes can consume ~1-10KB each depending on type
        const estimatedNodeMB = (audioNodes * 0.005).toFixed(1); // Assume 5KB average
        audioInfo += ` ~${estimatedNodeMB}MB`;
      }
    } catch (e) {
      audioInfo = "Error";
    }

    return {
      fps: Math.ceil(1000 / this.averageDuration),
      fps2: this.game!.getScreenFps(),
      bodyCount: world?.bodies.length ?? 0,
      hugeBodyCount: broadphase.hugeBodies?.size ?? 0,
      dynamicBodyCount: broadphase.dynamicBodies.size,
      kinematicBodyCount: broadphase.kinematicBodies.size,
      particleBodyCount: broadphase.particleBodies.size,
      entityCount: this.game?.entities.all.size ?? 0,
      spriteCount: getSpriteCount(this.game!.renderer.stage),
      collisions: (this.game?.world.broadphase as SpatialHashingBroadphase)
        .debugData.numCollisions,
      contacts: this.game?.contactList.getContacts().length ?? 0,
      memoryUsed,
      memoryTotal,
      gpuMemory,
      webglInfo,
      audioInfo,
    };
  }

  onKeyDown({ key }: GameEvents["keyDown"]) {
    if (key === "Backquote") {
      this.visible = !this.visible;
    }
  }
}

/** Counts the number of children of a display object. */
function getSpriteCount(root: Container): number {
  let total = 1;

  for (const child of root.children ?? []) {
    total += getSpriteCount(child);
  }

  return total;
}
